import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import ExcelJS from "exceljs";
import { Button } from "../components/ui/button";
import { Edit, Download, Menu, Expand } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "../components/ui/pagination";
import { AutoSizeInput } from "./ui/AutoSizeInput";

type ExcelEditorProps = {
  file: any;
  content: string | ArrayBuffer;
  onRename: (newName: string) => Promise<void>;
  isRenaming: boolean;
  isStudent: boolean;
  isSidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  isDarkMode?: boolean;
};

const getNameAndExt = (fullName: string) => {
  const lastDot = fullName.lastIndexOf(".");
  if (lastDot === -1) return { name: fullName, ext: "" };
  return {
    name: fullName.substring(0, lastDot),
    ext: fullName.substring(lastDot),
  };
};

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const cleaned = base64.trim();
  const binaryString = atob(cleaned);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

const ExcelEditor: React.FC<ExcelEditorProps> = ({
  file,
  content,
  onRename,
  isRenaming: isRenamingProp,
  isStudent,
  isSidebarCollapsed,
  onToggleSidebar,
  isDarkMode = false,
}) => {
  const [data, setData] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [activeSheet, setActiveSheet] = useState<string>("");

  const [isRenaming, setIsRenaming] = useState(false);
  const [fileName, setFileName] = useState(file.name);
  const { name: originalName, ext: fileExt } = getNameAndExt(file.name);
  const [nameOnly, setNameOnly] = useState(originalName);

  // Sync filename when file prop changes
  useEffect(() => {
    setFileName(file.name);
    const { name } = getNameAndExt(file.name);
    setNameOnly(name);
  }, [file.name]);
  const [page, setPage] = useState<number>(1);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState<boolean>(false);
  const pageSize = 20;

  // keep workbook in memory so we don't re-parse for each sheet change
  const workbookRef = useRef<ExcelJS.Workbook | null>(null);

  // Keep refs to cancel scheduled work
  const idleHandleRef = useRef<number | null>(null);
  const timeoutHandleRef = useRef<NodeJS.Timeout | number | null>(null);

  const scheduleHeavy = (fn: () => void) => {
    if ("requestIdleCallback" in window) {
      idleHandleRef.current = (window as any).requestIdleCallback(fn, {
        timeout: 200,
      });
      return;
    }
    timeoutHandleRef.current = setTimeout(fn, 0);
  };

  const cancelScheduled = () => {
    if (idleHandleRef.current != null && "cancelIdleCallback" in window) {
      (window as any).cancelIdleCallback(idleHandleRef.current);
    }
    if (timeoutHandleRef.current != null) {
      clearTimeout(timeoutHandleRef.current);
    }
    idleHandleRef.current = null;
    timeoutHandleRef.current = null;
  };

  const loadSheet = (workbook: ExcelJS.Workbook, sheetName: string) => {
    const ws = workbook.getWorksheet(sheetName);
    if (!ws) {
      setHeaders([]);
      setData([]);
      return;
    }

    const rows: string[][] = [];

    ws.eachRow((row) => {
      const values = row.values as any[]; // exceljs is 1-based; index 0 is dummy
      const cells = values
        .slice(1)
        .map((v) => (v === undefined || v === null ? "" : String(v)));
      rows.push(cells);
    });

    if (rows.length > 0) {
      const hdr = rows[0];
      const body = rows.slice(1);
      setHeaders(hdr);
      setData(body);
    } else {
      setHeaders([]);
      setData([]);
    }
    setPage(1);
  };

  useEffect(() => {
    // reset
    cancelScheduled();
    setParseError(null);
    setHeaders([]);
    setData([]);
    setSheetNames([]);
    setActiveSheet("");
    workbookRef.current = null;
    setPage(1);

    if (!content) {
      setParseError("No content available for this file.");
      return;
    }

    setIsParsing(true); // show loader immediately

    scheduleHeavy(() => {
      (async () => {
        try {
          if (typeof content === "string" && content.trim() === "") {
            setParseError("empty content for the file.");
            return;
          }

          const buffer: ArrayBuffer =
            typeof content === "string"
              ? base64ToArrayBuffer(content)
              : (content as ArrayBuffer);

          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(buffer);

          workbookRef.current = workbook;

          const sheets = workbook.worksheets.map((ws) => ws.name);
          setSheetNames(sheets);

          if (sheets.length > 0) {
            loadSheet(workbook, sheets[0]);
            setActiveSheet(sheets[0]);
          } else {
            setParseError("Workbook has no sheets.");
          }
        } catch (e) {
          console.error("Error parsing Excel file:", e);
          setParseError("Could not parse this Excel file.");
        } finally {
          setIsParsing(false);
        }
      })();
    });

    return () => {
      cancelScheduled();
    };
  }, [content]);

  const handleSheetChange = (sheetName: string) => {
    if (!workbookRef.current) return;
    setIsParsing(true);

    scheduleHeavy(() => {
      try {
        loadSheet(workbookRef.current as ExcelJS.Workbook, sheetName);
        setActiveSheet(sheetName);
      } catch (e) {
        console.error("Error switching sheet:", e);
        setParseError("Could not load the selected sheet.");
      } finally {
        setIsParsing(false);
      }
    });
  };

  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));

  const handleRename = async () => {
    const trimmedName = nameOnly.trim();
    const newFullName = trimmedName + fileExt;
    if (!trimmedName || newFullName === fileName) {
      setIsRenaming(false);
      setNameOnly(getNameAndExt(fileName).name);
      return;
    }
    const temp = fileName;
    setFileName(newFullName);
    try {
      await onRename(newFullName);
      setNameOnly(getNameAndExt(newFullName).name);
    } catch (error) {
      setFileName(temp);
      setNameOnly(getNameAndExt(fileName).name);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDownload = () => {
    try {
      let blob: Blob;
      if (typeof content === "string") {
        // decode base64 content directly to Blob
        const buffer = base64ToArrayBuffer(content);
        blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      } else {
        blob = new Blob([content as ArrayBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download failed:", e);
    }
  };

  // Error/empty or parsing states
  if (!content || parseError) {
    return (
      <div className="flex flex-col h-full bg-white dark:bg-[#0d1117]">
        <div className="flex items-center justify-between gap-2 px-4 py-2 border-b dark:border-[#30363d] dark:bg-[#161b22]">
          <div className="flex-shrink-0 flex items-center p-2 border-b dark:border-[#30363d] bg-gray-50 dark:bg-[#0d1117]">
            {isRenaming ? (
              <div className="flex items-center">
                <AutoSizeInput
                  type="text"
                  value={nameOnly}
                  onChange={(e) => setNameOnly(e.target.value)}
                  onBlur={handleRename}
                  onKeyDown={(e) => e.key === "Enter" && handleRename()}
                  className="font-medium text-gray-800 dark:text-[#f0f6fc] ml-2 focus:outline-none bg-transparent"
                  autoFocus
                  disabled={isRenamingProp}
                />
                <span className="font-medium text-gray-800 dark:text-[#f0f6fc]">{fileExt}</span>
                {isRenamingProp && (
                  <div className="ml-2 h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
                )}
              </div>
            ) : (
              <>
                <h3 
                  className="font-medium text-gray-800 dark:text-[#f0f6fc] ml-2 cursor-pointer hover:text-blue-600 dark:hover:text-[#f0883e] transition-colors select-none"
                  onDoubleClick={() => setIsRenaming(true)}
                  title="Double click to rename"
                >
                  {fileName}
                </h3>
                {isStudent && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsRenaming(true)}
                    className="dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
                  >
                    {isRenamingProp ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                    ) : (
                      <Edit className="text-gray-800 dark:text-[#c9d1d9]" size={14} />
                    )}
                  </Button>
                )}
              </>
            )}
          </div>
          <Button 
            variant="outline" 
            onClick={handleDownload}
            className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
          >
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
        </div>

        {isParsing ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
              <svg
                className="animate-spin h-5 w-5 text-gray-500"
                viewBox="0 0 24 24"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  opacity="0.25"
                />
                <path
                  d="M4 12a8 8 0 018-8"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  className="opacity-75"
                />
              </svg>
              <span>
                Loading Excel file… Large files may take several seconds.
              </span>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center p-6 text-sm text-muted-foreground dark:text-[#8b949e]">
            {parseError || "No content available for this file."}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-[#0d1117]">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b dark:border-[#30363d] dark:bg-[#161b22]">
        <div className="flex-shrink-0 flex items-center p-2">
          <Button
            variant="ghost"
            onClick={onToggleSidebar}
            className=" inline-flex items-center border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#0d1117] text-xs text-gray-700 dark:text-[#c9d1d9] hover:bg-gray-100 dark:hover:bg-[#21262d]"
            title={isSidebarCollapsed ? "Explorer" : "Hide Explorer"}
          >
            {isSidebarCollapsed ? (
              <>
                <Menu className="h-5 w-5" />
              </>
            ) : (
              <>
                <Expand className="h-4 w-4" />
              </>
            )}
          </Button>
          {isRenaming ? (
            <div className="flex items-center">
              <AutoSizeInput
                type="text"
                value={nameOnly}
                onChange={(e) => setNameOnly(e.target.value)}
                onBlur={handleRename}
                onKeyDown={(e) => e.key === "Enter" && handleRename()}
                className="font-medium text-gray-800 dark:text-[#f0f6fc] ml-2 focus:outline-none bg-transparent"
                autoFocus
                disabled={isRenamingProp}
              />
              <span className="font-medium text-gray-800 dark:text-[#f0f6fc]">{fileExt}</span>
              {isRenamingProp && (
                <div className="ml-2 h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
              )}
            </div>
          ) : (
            <>
              <h3 
                className="font-medium text-gray-800 dark:text-[#f0f6fc] ml-2 cursor-pointer hover:text-blue-600 dark:hover:text-[#f0883e] transition-colors select-none"
                onDoubleClick={() => setIsRenaming(true)}
                title="Double click to rename"
              >
                {fileName}
              </h3>
              {isStudent && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsRenaming(true)}
                  className="dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
                >
                  {isRenamingProp ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                  ) : (
                    <Edit className="text-gray-800 dark:text-[#c9d1d9]" size={14} />
                  )}
                </Button>
              )}
            </>
          )}
        </div>

        <Button 
          variant="outline" 
          onClick={handleDownload}
          className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
        >
          <Download className="mr-2 h-4 w-4" />
          Download
        </Button>
      </div>

      {/* Sheet Tabs */}
      {sheetNames.length > 1 && (
        <div className="flex items-center gap-2 px-4 py-2 border-b dark:border-[#30363d] overflow-x-auto dark:bg-[#161b22]">
          {sheetNames.map((sheet) => (
            <Button
              key={sheet}
              variant={sheet === activeSheet ? "default" : "ghost"}
              size="sm"
              onClick={() => handleSheetChange(sheet)}
              className={cn(
                sheet === activeSheet 
                  ? "dark:bg-[#f0883e] dark:text-white" 
                  : "dark:text-[#c9d1d9] dark:hover:bg-[#21262d]"
              )}
            >
              {sheet}
            </Button>
          ))}
        </div>
      )}

      {/* Parsing overlay */}
      {isParsing && (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
            <span>Loading Excel file…</span>
          </div>
        </div>
      )}

      {/* Table */}
      {!isParsing && (
        <div className="flex-1 overflow-auto">
          <table className="min-w-max text-sm w-full">
            <thead className="sticky top-0 bg-white dark:bg-[#161b22] z-10">
              <tr className="border-b dark:border-[#30363d]">
                {headers.map((header, index) => (
                  <th
                    key={index}
                    className="px-3 py-2 text-left font-semibold whitespace-nowrap dark:text-[#f0f6fc] border-r dark:border-[#30363d] last:border-r-0"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data
                .slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize)
                .map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-b dark:border-[#30363d] hover:bg-muted/30 dark:hover:bg-[#21262d]">
                    {row.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        className="px-3 py-2 whitespace-nowrap dark:text-gray-300 border-r dark:border-[#30363d] last:border-r-0"
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!isParsing && (
        <div className="border-t dark:border-[#30363d] px-4 py-2 dark:bg-[#161b22]">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground dark:text-[#8b949e]">
              Page {page} of {totalPages} • Showing{" "}
              {Math.min(
                pageSize,
                Math.max(0, data.length - (page - 1) * pageSize),
              )}{" "}
              of {data.length} rows
            </div>

            <Pagination className="border-0">
              <PaginationContent className="dark:text-[#c9d1d9]">
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    className="dark:hover:bg-[#21262d]"
                  />
                </PaginationItem>

                {page > 2 && (
                  <>
                    <PaginationItem>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setPage(1);
                        }}
                        className={cn(page === 1 && "dark:bg-[#f0883e] dark:text-white")}
                      >
                        1
                      </PaginationLink>
                    </PaginationItem>
                    {page > 3 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}
                  </>
                )}

                {Array.from(
                  {
                    length: Math.min(3, totalPages),
                  },
                  (_, i) => i + Math.max(1, page - 1),
                )
                  .filter((p) => p <= totalPages)
                  .map((p) => (
                    <PaginationItem key={p}>
                      <PaginationLink
                        href="#"
                        isActive={p === page}
                        onClick={(e) => {
                          e.preventDefault();
                          setPage(p);
                        }}
                        className={cn(p === page && "dark:bg-[#f0883e] dark:text-white", p !== page && "dark:hover:bg-[#21262d]")}
                      >
                        {p}
                      </PaginationLink>
                    </PaginationItem>
                  ))}

                {page < totalPages - 1 && (
                  <>
                    {page < totalPages - 2 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}
                    <PaginationItem>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setPage(totalPages);
                        }}
                        className={cn(page === totalPages && "dark:bg-[#f0883e] dark:text-white")}
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  </>
                )}

                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((p) => Math.min(totalPages, p + 1));
                    }}
                    className="dark:hover:bg-[#21262d]"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelEditor;
